import React, {useState, useEffect} from 'react';
import Head from "next/head";
import {getSearchiDealTypes} from "@models/property";
import {getFile} from "@models/media";
import {getPropertyUrl} from "@organisms/property/helpers/property_helpers";
import {isEmpty, str_replace} from "@helpers/helpers";
import {inArray, is_array} from "@helpers/arrays";
import {handleTitle} from "../../organisms/property/helpers/property_helpers";
import {getPropertyDetails, getPropertyTypes} from "../../../models/property";

const PropertiesSchema = ({properties}) => {

    return (
        <Head>
            {
                getStructuredData(properties).map((schema, key) =>
                    <script
                        key={key}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
                    />
                )
            }
        </Head>
    );
};

export default React.memo(PropertiesSchema);

function getStructuredData(properties) {
    let items = [];

    if (properties.length) {
        for (let property of properties) {
            const schema = getPropertyAbstract(property);

            items.push(schema)
        }
    }

    return items;
}

function getPropertyAbstract(property) {
    let property_type = property.type;
    if (property_type.indexOf('.') !== -1) {
        const mergedTypes = property_type.split('.');
        property_type = mergedTypes[0];
    }

    const propertyType = getPropertyTypes(property_type);

    const propertyDealType = getSearchiDealTypes(property.deal_type);

    const schemaCategory = propertyDealType + ' ' + propertyType;
    const schemaName = property.title;
    const schemaUrl = process.env.NEXT_PUBLIC_APP_URL + getPropertyUrl(property);

    let thumbnail = null;
    if (!isEmpty(property.thumbnail)) {
        thumbnail = property.thumbnail
    } else if (!isEmpty(property.media) && is_array(property.media) && property.media.length) {
        thumbnail = property.media[0].file_name;
    }

    const imageObjectSchema = getImageObjectSchema(property, thumbnail);

    const propertyDetails = getPropertyDetails(property, 6);
    let descriptionSchema = handleTitle(property) + ' ';

    for (let detail of propertyDetails) {
        descriptionSchema += ' . ' + detail[1];
    }

    const skuSchema = 1;
    const aggregateRatingSchema = getAggregateRating();
    const brandSchema = getBrandSchema();
    const offerSchema = getOfferSchema(property);

    const propertySchemaType = getSchemaType(property);

    const numberOfRooms = property.bedroom_count;

    let floorSize = null;
    if (propertySchemaType !== 'ApartmentComplex' || propertySchemaType !== 'Store') {
        floorSize = property.floor_count;
    }

    const getPostalAddress = getPostalAddressSchema(property);

    let geoCoordinatesSchema = null;
    if (!isEmpty(property.location)) {
        geoCoordinatesSchema = getGeoCoordinatesSchema(property, getPostalAddress)
    }


    let structuredData = {
        "@context": "https://schema.org",
        "@type": "Product",
        "type": propertySchemaType,
        "category": schemaCategory,
        "name": schemaName,
        "url": schemaUrl,
        "description": descriptionSchema,
        "sku": skuSchema,
        "numberOfRooms": numberOfRooms,
        "image": imageObjectSchema,
        "aggregateRating": aggregateRatingSchema,
        "brand": brandSchema,
        "offers": offerSchema,
        "address": getPostalAddress,
    };

    if (!isEmpty(floorSize)) {
        structuredData['floorSize'] = floorSize
    }

    if (!isEmpty(geoCoordinatesSchema)) {
        structuredData['geo'] = geoCoordinatesSchema
    }

    return structuredData;
}

function getSchemaType(property) {
    let propertySchemaType = 'Accommodation';

    switch (property.type) {
        case 'apartment':
        case 'office':
            propertySchemaType = 'Apartment';
            break;
        case 'house':
            propertySchemaType = 'House';
            break;
        case 'suite':
            propertySchemaType = 'Suite';
            break;
        case 'commercial':
            propertySchemaType = 'Store';
            break;
        case 'residential_complex':
            propertySchemaType = 'ApartmentComplex';
            break;
    }

    return propertySchemaType;
}

function getImageObjectSchema(property, thumbnail) {
    let images = 'https://www.2nabsh.com/_img/default/property.jpg';

    if (!isEmpty(thumbnail)) {
        const file_name = str_replace('.jpg', '', thumbnail.file_name)

        images = [
            getFile('/properties/' + property.id + '/' + file_name + '-thumb.jpg'),
            getFile('/properties/' + property.id + '/' + file_name + '.jpg'),
        ];
    }

    return {
        "@type": "ImageObject",
        "url": images,
    };
}

function getAggregateRating() {

    return {
        "@type": "AggregateRating",
        "ratingValue": 5,
        "ratingCount": 1,
    };
}

function getBrandSchema() {

    return {
        "@type": "Brand",
        "url": 'https://www.2nabsh.com/',
        "name": '۲نبش',
        "alternateName": '2nabsh',
        "logo": {
            "@type": "ImageObject",
            "url": "https://www.2nabsh.com/img/logo.png\n",
        }
    };
}

function getOfferSchema(property) {
    let availability = 'http://schema.org/InStock';

    if (property.status === 'dealt') {
        availability = 'http://schema.org/SoldOut';
    } else if (property.status === 'expired' || property.archived) {
        availability = 'http://schema.org/Discontinued';
    }

    const propertyUrl = process.env.NEXT_PUBLIC_APP_URL + getPropertyUrl(property);
    let businessFunction = 'http://purl.org/goodrelations/v1#Sell';

    let price = 0;
    if (inArray(property.deal_type, ['sale', 'pre_sale', 'exchange', 'participation'])) {
        price = property.price;
    } else if (inArray(property.deal_type, ['rent', 'mortgage'])) {
        if (!isEmpty(property.rent)) {
            price = property.rent;
        } else if (!isEmpty(property.mortgage)) {
            price = property.mortgage * 1000;
        }
        businessFunction = 'http://purl.org/goodrelations/v1#LeaseOut';
    }

    const date = new Date((property.boosted_at + 5356800) * 1000); // add two month
    const dateString = date.toGMTString();

    return {
        "@type": "Offer",
        "availability": availability,
        "url": propertyUrl,
        "businessFunction": businessFunction,
        "price": price,
        "priceCurrency": "IRR",
        "priceValidUntil": dateString,
    };
}

function getPostalAddressSchema(property) {
    return {
        "@type": "PostalAddress",
        "addressLocality": property.city_name,
        "addressRegion": property.district_name,
        "streetAddress": property.street,
        "areaServed": 'IR',
    };
}

function getGeoCoordinatesSchema(property, getPostalAddress) {
    return {
        "@type": "GeoCoordinates",
        "latitude": property.location.latitude,
        "longitude": property.location.longitude,
        "address": getPostalAddress,
        "url": 'https://www.google.com/maps/@' + property.location.latitude + ',' + property.location.longitude + ',16z',
    };
}
